package br.com.vvrefeicoes.portal.feature.dashboard

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.ui.theme.cardElevation
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.HomeAnnouncement
import br.com.vvrefeicoes.portal.domain.model.HomeData
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.allows
import br.com.vvrefeicoes.portal.feature.notifications.AnnouncementImage
import br.com.vvrefeicoes.portal.feature.notifications.AnnouncementImageLightbox
import br.com.vvrefeicoes.portal.feature.notifications.RichBody
import br.com.vvrefeicoes.portal.navigation.OperationalEntriesRoute
import br.com.vvrefeicoes.portal.navigation.OperationalPanelRoute
import br.com.vvrefeicoes.portal.navigation.PurchasesEntriesRoute
import br.com.vvrefeicoes.portal.navigation.PurchasesRoute
import com.composables.icons.lucide.ArrowRight
import com.composables.icons.lucide.ChartColumn
import com.composables.icons.lucide.CircleCheck
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.ShoppingCart
import com.composables.icons.lucide.TriangleAlert
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.dashboard_hello
import portalvv.shared.generated.resources.home_empty_updates
import portalvv.shared.generated.resources.home_go_entries
import portalvv.shared.generated.resources.home_go_panel
import portalvv.shared.generated.resources.home_operational
import portalvv.shared.generated.resources.home_pending_month
import portalvv.shared.generated.resources.home_purchases
import portalvv.shared.generated.resources.home_ready
import portalvv.shared.generated.resources.home_retry
import portalvv.shared.generated.resources.home_updates_title
import portalvv.shared.generated.resources.undrawable_empty
import kotlin.time.Instant

private val ModuleColumnWidth = 360.dp
private val CompactBreakpoint = 1080.dp

@Composable
fun DashboardScreen(onNavigate: (Any) -> Unit) {
    val appGraph = LocalAppGraph.current
    val session by appGraph.authRepository.session.collectAsStateWithLifecycle()
    val permissions = rememberSessionPermissions()
    var today by remember { mutableStateOf<LocalDate?>(null) }
    var data by remember { mutableStateOf<HomeData?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var refresh by remember { mutableIntStateOf(0) }

    LaunchedEffect(Unit) {
        today = runCatching { appGraph.systemRepository.today() }.getOrNull()
    }

    LaunchedEffect(refresh) {
        loading = true
        error = null
        try {
            data = appGraph.homeRepository.load()
        } catch (throwable: Throwable) {
            error = throwable.message ?: "Não foi possível carregar a página inicial."
        } finally {
            loading = false
        }
    }

    val hasOperational = permissions.allows(PermissionKeys.OPERATIONAL_ENTRIES, PermissionAction.CREATE)
    val hasPurchases = permissions.allows(PermissionKeys.PURCHASES_ENTRIES, PermissionAction.CREATE)
    val canOpenOperationalPanel = permissions.allows(PermissionKeys.OPERATIONAL_PANEL, PermissionAction.VIEW)
    val canOpenPurchasesPanel = permissions.allows(PermissionKeys.PURCHASES, PermissionAction.VIEW)

    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space32),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space24),
        footer = { AppFooter() },
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            val compact = maxWidth < CompactBreakpoint
            val headerCompact = maxWidth < 720.dp
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space24)) {
                Header(
                    name = session?.displayName.orEmpty(),
                    date = today,
                    compact = headerCompact,
                )

                if (loading) {
                    Box(Modifier.fillMaxWidth().heightIn(min = 320.dp), contentAlignment = Alignment.Center) {
                        CircularProgressIndicator()
                    }
                } else if (error != null) {
                    ErrorState(
                        message = error.orEmpty(),
                        onRetry = { refresh++ },
                    )
                } else if (compact) {
                    if (hasOperational || hasPurchases) {
                        ModuleCards(
                            data = data,
                            hasOperational = hasOperational,
                            hasPurchases = hasPurchases,
                            canOpenOperationalPanel = canOpenOperationalPanel,
                            canOpenPurchasesPanel = canOpenPurchasesPanel,
                            onNavigate = onNavigate,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                    SectionTitle()
                    Announcements(data?.announcements.orEmpty(), today, Modifier.fillMaxWidth())
                } else {
                    SectionTitle()
                    if (hasOperational || hasPurchases) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space32),
                            verticalAlignment = Alignment.Top,
                        ) {
                            Announcements(
                                announcements = data?.announcements.orEmpty(),
                                today = today,
                                modifier = Modifier.weight(1f),
                            )
                            ModuleCards(
                                data = data,
                                hasOperational = hasOperational,
                                hasPurchases = hasPurchases,
                                canOpenOperationalPanel = canOpenOperationalPanel,
                                canOpenPurchasesPanel = canOpenPurchasesPanel,
                                onNavigate = onNavigate,
                                modifier = Modifier.width(ModuleColumnWidth),
                            )
                        }
                    } else {
                        Announcements(
                            announcements = data?.announcements.orEmpty(),
                            today = today,
                            modifier = Modifier.fillMaxWidth(),
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun Header(
    name: String,
    date: LocalDate?,
    compact: Boolean,
) {
    if (compact) {
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            Text(
                text = stringResource(Res.string.dashboard_hello, name),
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.SemiBold,
            )
            date?.let {
                Text(
                    text = formatLongDate(it),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    } else {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = stringResource(Res.string.dashboard_hello, name),
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
            )
            date?.let {
                Text(
                    text = formatLongDate(it),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun SectionTitle() {
    Text(
        text = stringResource(Res.string.home_updates_title),
        style = MaterialTheme.typography.titleLarge,
        fontWeight = FontWeight.SemiBold,
    )
}

@Composable
private fun Announcements(
    announcements: List<HomeAnnouncement>,
    today: LocalDate?,
    modifier: Modifier = Modifier,
) {
    if (announcements.isEmpty()) {
        EmptyUpdates(modifier = modifier)
        return
    }
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space24),
    ) {
        announcements.forEach { announcement ->
            AnnouncementCard(announcement = announcement, today = today)
        }
    }
}

@Composable
private fun AnnouncementCard(
    announcement: HomeAnnouncement,
    today: LocalDate?,
) {
    var expandedImage by remember(announcement.id) { mutableStateOf<String?>(null) }
    Surface(
        modifier = Modifier.fillMaxWidth().cardElevation(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space24),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        ) {
            Text(
                text = announcement.title,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.SemiBold,
            )
            Text(
                text = publishedLabel(announcement, today),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            announcement.imageUrl?.takeIf { it.isNotBlank() }?.let { image ->
                Box(Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                    AnnouncementImage(
                        source = image,
                        modifier =
                            Modifier
                                .widthIn(max = 320.dp)
                                .heightIn(max = 360.dp)
                                .clickable { expandedImage = image },
                    )
                }
            }
            if (announcement.body.isNotBlank()) {
                RichBody(html = announcement.body, modifier = Modifier.fillMaxWidth())
            }
        }
    }
    expandedImage?.let { image ->
        AnnouncementImageLightbox(imageUrl = image, onDismiss = { expandedImage = null })
    }
}

@Composable
private fun EmptyUpdates(modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.fillMaxWidth().padding(AppSpacing.space32),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space24),
    ) {
        Image(
            painter = painterResource(Res.drawable.undrawable_empty),
            contentDescription = null,
            modifier = Modifier.widthIn(max = 480.dp).fillMaxWidth(0.8f).heightIn(max = 240.dp),
        )
        Text(
            text = stringResource(Res.string.home_empty_updates),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}

@Composable
private fun ModuleCards(
    data: HomeData?,
    hasOperational: Boolean,
    hasPurchases: Boolean,
    canOpenOperationalPanel: Boolean,
    canOpenPurchasesPanel: Boolean,
    onNavigate: (Any) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
    ) {
        if (hasOperational) {
            ModuleCard(
                title = stringResource(Res.string.home_operational),
                icon = Lucide.ChartColumn,
                complete = data?.operationalComplete == true,
                onAction =
                    if (data?.operationalComplete == true) {
                        if (canOpenOperationalPanel) ({ onNavigate(OperationalPanelRoute) }) else null
                    } else {
                        { onNavigate(OperationalEntriesRoute) }
                    },
            )
        }
        if (hasPurchases) {
            ModuleCard(
                title = stringResource(Res.string.home_purchases),
                icon = Lucide.ShoppingCart,
                complete = data?.purchasesComplete == true,
                onAction =
                    if (data?.purchasesComplete == true) {
                        if (canOpenPurchasesPanel) ({ onNavigate(PurchasesRoute) }) else null
                    } else {
                        { onNavigate(PurchasesEntriesRoute) }
                    },
            )
        }
    }
}

@Composable
private fun ModuleCard(
    title: String,
    icon: ImageVector,
    complete: Boolean,
    onAction: (() -> Unit)?,
) {
    Surface(
        modifier = Modifier.fillMaxWidth().cardElevation(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space20),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            ) {
                Box(
                    modifier = Modifier.size(40.dp).background(AppPalette.Primary100, CircleShape),
                    contentAlignment = Alignment.Center,
                ) {
                    Icon(imageVector = icon, contentDescription = null, tint = AppPalette.Primary500)
                }
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold,
                )
            }
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                Icon(
                    imageVector = if (complete) Lucide.CircleCheck else Lucide.TriangleAlert,
                    contentDescription = null,
                    tint = if (complete) AppPalette.Success500 else AppPalette.Warning500,
                    modifier = Modifier.size(20.dp),
                )
                Text(
                    text =
                        if (complete) {
                            stringResource(Res.string.home_ready)
                        } else {
                            stringResource(Res.string.home_pending_month)
                        },
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            if (onAction != null) {
                Row(
                    modifier =
                        Modifier
                            .align(Alignment.End)
                            .clickable(onClick = onAction)
                            .padding(vertical = AppSpacing.space4),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
                ) {
                    Text(
                        text =
                            if (complete) {
                                stringResource(Res.string.home_go_panel)
                            } else {
                                stringResource(Res.string.home_go_entries)
                            },
                        style = MaterialTheme.typography.titleSmall,
                        color = AppPalette.Primary500,
                    )
                    Icon(
                        imageVector = Lucide.ArrowRight,
                        contentDescription = null,
                        tint = AppPalette.Primary500,
                        modifier = Modifier.size(18.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun ErrorState(
    message: String,
    onRetry: () -> Unit,
) {
    Column(
        modifier = Modifier.fillMaxWidth().padding(AppSpacing.space32),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
    ) {
        Text(
            text = message,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.error,
        )
        Text(
            text = stringResource(Res.string.home_retry),
            style = MaterialTheme.typography.titleSmall,
            color = AppPalette.Primary500,
            modifier = Modifier.clickable(onClick = onRetry).padding(AppSpacing.space8),
        )
    }
}

private fun publishedLabel(
    announcement: HomeAnnouncement,
    today: LocalDate?,
): String {
    val value = announcement.publishedAt ?: return ""
    val local =
        runCatching { Instant.parse(value).toLocalDateTime(TimeZone.currentSystemDefault()) }
            .getOrNull()
            ?: return ""
    val date = local.date
    val time = "${pad(local.hour)}:${pad(local.minute)}"
    val whenLabel =
        when {
            today != null && date == today -> "hoje"
            today != null && date == today.minus(1, DateTimeUnit.DAY) -> "ontem"
            else -> "${pad(date.day)}/${pad(date.month.number)}/${date.year}"
        }
    return "Publicado $whenLabel às $time por ${announcement.authorName}"
}

private fun pad(value: Int): String = if (value < 10) "0$value" else value.toString()

private val WEEKDAYS =
    listOf(
        "segunda-feira",
        "terça-feira",
        "quarta-feira",
        "quinta-feira",
        "sexta-feira",
        "sábado",
        "domingo",
    )

private fun formatLongDate(date: LocalDate): String {
    val weekday = WEEKDAYS[date.dayOfWeek.ordinal]
    val month = MonthCalendar.monthName(date.month.ordinal + 1).lowercase()
    return "$weekday, ${date.day} de $month de ${date.year}"
}
